/* ══════════════════════════════════════════════════════
   Kartenkiste – app logic (lokal, ohne KI)
   Speicher: IndexedDB (Karten, Decks, Kurse, Protokoll, Meta)
   ══════════════════════════════════════════════════════ */

export const DAY = 86400000;

export const uid = () => Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
export const $ = (s) => document.querySelector(s);
export const $$ = (s) => Array.from(document.querySelectorAll(s));
export const esc = (s) => String(s ?? '').replace(/[&<>"]/g, (c) =>
  ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/* ══════════ Rich-Text: Formeln ($…$ / $$…$$), Code (`…` / ```…```),
   **fett**, *kursiv* und Lückentext-Marker {{…}}.
   opt.cloze === true  → Antworten einblenden (Lösungsseite)
   opt.cloze === false → Lücken [ … ] zeigen (Frageseite)              */
export function renderMath(tex, block) {
  if (window.katex) {
    try { return window.katex.renderToString(tex, { displayMode: block, throwOnError: false }); } catch (e) { /* fällt durch */ }
  }
  return `<code class="cc-ic">${esc((block ? '$$' : '$') + tex + (block ? '$$' : '$'))}</code>`;
}
export function renderRich(raw, opt = {}) {
  if (raw == null || raw === '') return '';
  let s = String(raw);
  const math = [], code = [], cloze = [];
  const clozeMode = opt.cloze; // true=reveal, false=gap, undefined=ignore
  s = s.replace(/```([\s\S]*?)```/g, (_, c) => { code.push({ block: true, c: c.replace(/^\n/, '') }); return `C${code.length - 1}`; });
  s = s.replace(/\$\$([\s\S]+?)\$\$/g, (_, m) => { math.push({ block: true, m }); return `M${math.length - 1}`; });
  s = s.replace(/`([^`\n]+?)`/g, (_, c) => { code.push({ block: false, c }); return `C${code.length - 1}`; });
  s = s.replace(/\$([^$\n]+?)\$/g, (_, m) => { math.push({ block: false, m }); return `M${math.length - 1}`; });
  if (clozeMode !== undefined) {
    s = s.replace(/\{\{(?:c\d+::)?([\s\S]+?)\}\}/g, (_, ans) => { cloze.push(ans); return `Z${cloze.length - 1}`; });
  }
  s = esc(s);
  s = s.replace(/\*\*([^*\n]+?)\*\*/g, '<b>$1</b>').replace(/(^|[^*])\*([^*\n]+?)\*/g, '$1<i>$2</i>');
  s = s.replace(/\n/g, '<br>');
  s = s.replace(/C(\d+)/g, (_, i) => { const { block, c } = code[+i]; return block ? `<pre class="cc-code">${esc(c)}</pre>` : `<code class="cc-ic">${esc(c)}</code>`; });
  s = s.replace(/M(\d+)/g, (_, i) => { const { block, m } = math[+i]; return renderMath(m, block); });
  s = s.replace(/Z(\d+)/g, (_, i) => clozeMode
    ? `<span class="cloze-a">${esc(cloze[+i])}</span>`
    : `<span class="cloze-gap">[&nbsp;…&nbsp;]</span>`);
  return s;
}
/* Nur die Klartext-Antworten eines Lückentexts, für Schreib-Modus & Vorschau */
export function clozeAnswers(text) {
  const out = []; String(text || '').replace(/\{\{(?:c\d+::)?([\s\S]+?)\}\}/g, (_, a) => { out.push(a.trim()); return ''; });
  return out.join(', ');
}
/* Marker/Markup für kurze Listen-Vorschauen entfernen */
export const stripMarkup = (t) => String(t || '').replace(/\{\{(?:c\d+::)?([\s\S]+?)\}\}/g, '[$1]').replace(/[`*$]/g, '');

/* ══════════ Vorlesen (Text-to-Speech, Web Speech API) ══════════ */
export const ttsOK = () => typeof window !== 'undefined' && 'speechSynthesis' in window;
export function germanVoices() {
  return (ttsOK() ? speechSynthesis.getVoices() : []).filter((v) => /^de\b|de[-_]/i.test(v.lang));
}
/* Beste deutsche Stimme wählen: gespeicherte Auswahl > „natürliche"/Online-Stimmen > Rest.
   Natürlichere Stimmen heißen oft „Google Deutsch", „…Natural", „…Online", „Katja", „Conrad". */
export function pickVoice() {
  const voices = germanVoices();
  if (!voices.length) return null;
  const saved = localStorage.getItem('ttsVoice');
  if (saved) { const m = voices.find((v) => v.name === saved); if (m) return m; }
  const score = (n) => {
    n = (n || '').toLowerCase();
    if (/natural|neural|online|katja|conrad|google/.test(n)) return 3;
    if (/hedda|stefan|marlene/.test(n)) return 1;
    return 0;
  };
  return voices.slice().sort((a, b) => score(b.name) - score(a.name))[0];
}
export function speak(text) {
  if (!ttsOK() || !text) return;
  try {
    speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(String(text).slice(0, 600));
    u.lang = 'de-DE'; u.rate = 0.95; u.pitch = 1.05;
    const v = pickVoice();
    if (v) u.voice = v;
    speechSynthesis.speak(u);
  } catch (e) { /* stumm ignorieren */ }
}
export function populateVoicePicker() {
  const sel = document.getElementById('opt-voice'); if (!sel) return;
  const voices = germanVoices();
  const cur = localStorage.getItem('ttsVoice') || '';
  sel.innerHTML = '<option value="">Automatisch (beste)</option>'
    + voices.map((v) => `<option value="${esc(v.name)}" ${v.name === cur ? 'selected' : ''}>${esc(v.name)}${v.localService ? '' : ' – online'}</option>`).join('');
  const note = document.getElementById('voice-note');
  if (note) note.textContent = voices.length
    ? 'Tipp: „online"-Stimmen (z. B. Google Deutsch) klingen meist natürlicher.'
    : 'Dein Browser bietet gerade keine deutsche Stimme an – oft hilft ein anderer Browser (z. B. Chrome/Edge).';
}
export function speakCurrent() {
  // Liest die gerade sichtbare Kartenseite vor (Vorder- oder Rückseite).
  const back = $('#flip-inner')?.classList.contains('flipped');
  const face = $(back ? '#face-back' : '#face-front');
  const el = face && face.querySelector('.face-text');
  speak(el ? el.textContent : '');
}

export const dayKey = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const fromKey = (k) => { const [y, m, d] = k.split('-').map(Number); return new Date(y, m - 1, d); };
export const startOfDay = (d = new Date()) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

export function fmtDur(ms) {
  if (!ms) return '0 min';
  const min = Math.round(ms / 60000);
  if (min < 60) return min + ' min';
  return Math.floor(min / 60) + ' h ' + (min % 60) + ' min';
}
export function fmtDue(card) {
  const now = Date.now();
  if (!card.due || card.due <= now) return 'jetzt fällig';
  const d = Math.round((card.due - now) / DAY);
  if (d < 1) return 'heute nochmal';
  if (d === 1) return 'morgen dran';
  return `in ${d} Tagen`;
}

